import { logOut, login, mfaSubmit, forgotPassword, confirmForgotPassword } from '@App/api/routes/auth'
import { action, observable, makeObservable } from 'mobx'
import { clearAppQueryCache } from '@App/configs/query-client'
import RootStore from '@App/stores/root-store'
import { TokenResponse } from '@Entities/auth/token-response'

/**
 * The access token lives in an HttpOnly cookie that the browser attaches to every
 * API request on its own, so nothing here reads, stores, decodes or expires a token.
 */
export default class AuthStore {
    private isLogged = false

    private rootStore: RootStore

    constructor(rootStore: RootStore) {
        makeObservable<AuthStore, 'isLogged'>(this, {
            isLogged: observable,
            setLoggedIn: action,
            logOut: action,
            clearLoginData: action,
        })

        this.rootStore = rootStore
    }

    get isLoggedIn() {
        return this.isLogged
    }

    setLoggedIn(loggedIn: boolean) {
        this.isLogged = loggedIn
    }

    async logOut() {
        const logOutRequest = logOut()
        this.clearLoginData()
        await logOutRequest
    }

    clearLoginData() {
        this.setLoggedIn(false)

        clearAppQueryCache()
        this.rootStore.resetStore()
    }

    async login(username: string, password: string): Promise<TokenResponse> {
        return new Promise((resolve, reject) => {
            const body = { username, password }
            login(body)
                .then(async (response) => {
                    const tokenResponse = response.data

                    if (tokenResponse.message === 'mfa_required' || tokenResponse.message === 'enroll_mfa') {
                        resolve(tokenResponse)
                        return
                    }

                    if (!tokenResponse.error && tokenResponse.message === '') {
                        // For users with MFA turned off (MPOR-1588)
                        this.setLoggedIn(true)
                        await this.loadDataOnLogin()
                        resolve(tokenResponse)
                        return
                    }

                    this.setLoggedIn(false)
                    resolve(tokenResponse)
                })
                .catch((error) => {
                    this.setLoggedIn(false)
                    this.rootStore.popupStore.setAlertData(error.message, true)
                    reject(error.message)
                })
        })
    }

    async mfaSubmit(code: string): Promise<void> {
        let tokenResponse: TokenResponse
        try {
            const response = await mfaSubmit({ code })
            tokenResponse = response.data
        } catch (ignore) {
            this.rootStore.popupStore.setAlertData(
                'MFA code entered is expired or invalid. Please contact Support for a new code.',
                true
            )
            throw new Error('MFA code entered is expired or invalid. Please contact Support for a new code.')
        }

        if (tokenResponse.error) {
            this.rootStore.popupStore.setAlertData('Unable To Confirm MFA', true)
            throw new Error('Unable To Confirm MFA')
        }

        await this.loadDataOnLogin()
        this.setLoggedIn(true)
    }

    async forgotPassword(username: string) {
        forgotPassword(username).catch((error) => this.rootStore.popupStore.setAlertData(error.message, true))
    }

    async confirmForgotPassword(username: string, confirmationCode: string, newPassword: string) {
        await confirmForgotPassword(username, confirmationCode, newPassword)
    }

    async checkUserAuthorized(): Promise<void> {
        try {
            await this.rootStore.userStore.loadUserInfo()
        } catch (ignore) {
            this.setLoggedIn(false)
            return
        }

        this.setLoggedIn(true)
        await this.loadSessionData()
    }

    private loadDataOnLogin = async () => {
        await this.rootStore.userStore.loadUserInfo()
        await this.loadSessionData()
    }

    private loadSessionData = async () => {
        await Promise.all([this.loadRightsholdersOnLogin(), this.rootStore.notificationStore.getNotificationToken()])
    }

    private loadRightsholdersOnLogin = async () => {
        if (!this.rootStore.userStore.isInternal) {
            await Promise.all([
                this.rootStore.rightsholderSearchStore.loadRightsholders({}),
                this.rootStore.songwriterSearchStore.loadSongwriters({}),
            ])
        }
    }
}
